import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useTimesheetMutations } from '@/hooks/timesheets';
import { useCompanyTimezone } from '@/hooks/useCompanyTimezone';
import {
  companyDatetimeLocalToISO,
  nowAsCompanyDatetimeLocal,
  companyTimezoneHint,
} from '@/lib/timesheetTime';

interface ManualEntryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ManualEntryDialog({ open, onOpenChange }: ManualEntryDialogProps) {
  const [clockIn, setClockIn] = useState('');
  const [clockOut, setClockOut] = useState('');
  const [reason, setReason] = useState('');
  const { createManualEntry } = useTimesheetMutations();
  const timezone = useCompanyTimezone();

  /** "Now" as a company-timezone wall clock, for the datetime-local `max`. */
  const getCompanyDateTimeMax = (): string => nowAsCompanyDatetimeLocal(timezone);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const trimmedReason = reason.trim();
    
    if (!clockIn || !clockOut || !trimmedReason) {
      return;
    }

    // Inputs are wall clocks in the company timezone -> absolute instants.
    const clockInISO = companyDatetimeLocalToISO(clockIn, timezone);
    const clockOutISO = companyDatetimeLocalToISO(clockOut, timezone);
    const clockInDate = new Date(clockInISO);
    const clockOutDate = new Date(clockOutISO);

    if (clockInDate >= clockOutDate) {
      return;
    }

    createManualEntry.mutate(
      {
        clockIn: clockInISO,
        clockOut: clockOutISO,
        reason: trimmedReason,
      },
      {
        onSuccess: () => {
          setClockIn('');
          setClockOut('');
          setReason('');
          onOpenChange(false);
        },
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Add Manual Time Entry</DialogTitle>
            <p className="text-xs text-muted-foreground">{companyTimezoneHint(timezone)}</p>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="clockIn">Clock In *</Label>
              <Input
                id="clockIn"
                type="datetime-local"
                value={clockIn}
                onChange={(e) => setClockIn(e.target.value)}
                max={getCompanyDateTimeMax()}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="clockOut">Clock Out *</Label>
              <Input
                id="clockOut"
                type="datetime-local"
                value={clockOut}
                onChange={(e) => setClockOut(e.target.value)}
                max={getCompanyDateTimeMax()}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="reason">Reason *</Label>
              <Textarea
                id="reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Enter reason for manual entry..."
                rows={3}
                maxLength={500}
                required
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={createManualEntry.isPending}>
              {createManualEntry.isPending ? 'Creating...' : 'Create Entry'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
